import { Autocomplete, Box, CircularProgress, InputAdornment, TextField, styled, Skeleton } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import debounce from 'lodash/debounce';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { ROUTES } from 'routes/const';
import { trim } from 'lodash';
import useFakeLoader from 'hooks/useFakeLoader';

const GroupHeader = styled('div')(({ theme }) => ({
  position: 'sticky',
  top: '-8px',
  padding: '4px 16px',
  color: '#7A7B84',
  backgroundColor: '#ffffff',
}));

const GroupItems = styled('ul')({
  padding: 0,
});

const OnechipsSearch = ({ data, loading, onSearch, onSelect, onSubmit }) => {
  const { t } = useTranslation();
  const initialLoading = useFakeLoader(1);

  const [inputValue, setInputValue] = useState('');
  const [value, setValue] = useState(null);
  const [options, setOptions] = useState([]);

  const navigate = useNavigate();

  const newData = useMemo(() => {
    return (data ?? []).map((item) => ({
      group: 'eIDs',
      eid: item,
    }));
  }, [data]);

  const debouncedSearch = useMemo(() => {
    const fn = debounce((value) => {
      if (value.trim().length !== 0) {
        onSearch(value);
      } else {
        setOptions([]);
      }
    }, 600);

    return fn;
  }, [onSearch]);

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  useEffect(() => {
    setOptions(newData);
  }, [newData]);

  const handleOnChange = (event, newValue, reason) => {
    if (reason === 'selectOption') {
      setValue(newValue);
      setInputValue(newValue?.eid ?? '');
      onSelect(newValue, reason);
    }
  };

  const handleOnInputChange = (event, value, reason) => {
    if (reason === 'input') {
      setInputValue(value);
      debouncedSearch(value);
    } else if (reason === 'clear') {
      setInputValue('');
      setValue(null);
      debouncedSearch.cancel();
      setOptions([]);
    }
  };

  return (
    <>
      {initialLoading ? (
        <Skeleton variant="rectangular" width={'350px'}>
          <Box alignItems="left" width={'100%'} height={'40px'} />
        </Skeleton>
      ) : (
        <>
          <Box sx={{ ml: 2, color: 'text.secondary' }}>All times in UTC</Box>

          <form onSubmit={onSubmit}>
            <Autocomplete
              sx={{
                width: { xs: '100%', md: 350 },
                height: '40px',
                '& .MuiOutlinedInput-root': {
                  paddingLeft: '10px',
                  paddingRight: '30px !important',
                },
              }}
              options={options}
              value={value}
              inputValue={inputValue}
              size="small"
              autoComplete
              noOptionsText={!loading ? 'No results found' : ''}
              groupBy={(option) => option.group}
              renderGroup={(params) => (
                <li key={params.key}>
                  <GroupHeader>{params.group}</GroupHeader>
                  <GroupItems>{params.children}</GroupItems>
                </li>
              )}
              getOptionLabel={(option) => (typeof option === 'string' ? option : option.eid)}
              onInputChange={handleOnInputChange}
              onChange={handleOnChange}
              loading={loading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  name="search"
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start" sx={{ pl: 1 }}>
                        <img src="/search_icon.svg" alt="" />
                      </InputAdornment>
                    ),
                    endAdornment: <>{loading && <CircularProgress color="inherit" size={20} />}</>,
                  }}
                  placeholder={t('common.top_bar.search_placeholder')}
                />
              )}
            />
          </form>
        </>
      )}
    </>
  );
};

export default OnechipsSearch;
